'use client';

import React, { useEffect, useId, useState } from 'react';
import { generateDisplacementMap } from './displacementMap';

/**
 * PillGlass — a refraction *layer* (not a box) for the clock pills.
 *
 * Unlike `LiquidGlass`, this renders only an `absolute inset-0` displaced copy
 * of the backdrop plus its SVG filter, so it drops inside ClusterMarker's
 * existing capsules without disturbing their tint / mask / home-ring machinery.
 * The host capsule supplies the border-radius (`overflow-hidden rounded-full`)
 * and the specular bevel.
 *
 * The backdrop copy is the conic clock-dial gradient, aligned to the slice
 * sitting behind this pill via explicit `backdropSize` / `backdropPosition`
 * computed by the caller from the pill's known dial geometry — no snapshot, no
 * per-frame DOM measurement. Applied via `filter:` (not `backdrop-filter`) so it
 * refracts on iOS Safari, matching the validated approach.
 */
export interface PillGlassProps {
  /** Pill width in px (for the displacement-map resolution). */
  width: number;
  /** Pill height in px. */
  height: number;
  /** Corner radius in px (pills are full-round → height/2). */
  radius: number;
  /** CSS background to refract (the dial conic gradient). */
  refractionBackground: string;
  /** `background-size` aligning the backdrop to how it's painted on the dial. */
  backdropSize: string;
  /** `background-position` offsetting the backdrop to this pill's slice. */
  backdropPosition: string;
  bezel: number;
  strength: number;
  aberration: number;
  blur?: number;
}

export function PillGlass({
  width,
  height,
  radius,
  refractionBackground,
  backdropSize,
  backdropPosition,
  bezel,
  strength,
  aberration,
  blur = 0,
}: PillGlassProps) {
  const rawId = useId();
  const filterId = `pillglass-${rawId.replace(/:/g, '')}`;
  const [mapUrl, setMapUrl] = useState('');

  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect -- displacement map needs the DOM canvas; must run after mount
    setMapUrl(generateDisplacementMap({ width, height, radius, bezel }));
  }, [width, height, radius, bezel]);

  return (
    <>
      <div
        aria-hidden
        className="absolute inset-0 rounded-full"
        style={{
          backgroundImage: refractionBackground,
          backgroundSize: backdropSize,
          backgroundPosition: backdropPosition,
          backgroundRepeat: 'no-repeat',
          filter: mapUrl
            ? `url(#${filterId})${blur ? ` blur(${blur}px)` : ''}`
            : 'none',
          opacity: mapUrl ? 1 : 0,
        }}
      />
      <svg
        width="0"
        height="0"
        aria-hidden
        style={{ position: 'absolute', pointerEvents: 'none' }}
      >
        <defs>
          <filter
            id={filterId}
            x="0"
            y="0"
            width="100%"
            height="100%"
            colorInterpolationFilters="sRGB"
          >
            {mapUrl && (
              <feImage
                href={mapUrl}
                x="0"
                y="0"
                width={width}
                height={height}
                result="map"
                preserveAspectRatio="none"
              />
            )}
            {/* Chromatic aberration: R displaced hardest, B softest, then the
                three channels recombined. Alpha kept on all three (SVG filters
                composite premultiplied — zeroing alpha would black out R/B). */}
            <feDisplacementMap
              in="SourceGraphic"
              in2="map"
              scale={strength + aberration}
              xChannelSelector="R"
              yChannelSelector="G"
              result="dispR"
            />
            <feColorMatrix
              in="dispR"
              type="matrix"
              values="1 0 0 0 0  0 0 0 0 0  0 0 0 0 0  0 0 0 1 0"
              result="chR"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="map"
              scale={strength}
              xChannelSelector="R"
              yChannelSelector="G"
              result="dispG"
            />
            <feColorMatrix
              in="dispG"
              type="matrix"
              values="0 0 0 0 0  0 1 0 0 0  0 0 0 0 0  0 0 0 1 0"
              result="chG"
            />
            <feDisplacementMap
              in="SourceGraphic"
              in2="map"
              scale={strength - aberration}
              xChannelSelector="R"
              yChannelSelector="G"
              result="dispB"
            />
            <feColorMatrix
              in="dispB"
              type="matrix"
              values="0 0 0 0 0  0 0 0 0 0  0 0 1 0 0  0 0 0 1 0"
              result="chB"
            />
            <feComposite in="chR" in2="chG" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" result="chRG" />
            <feComposite in="chRG" in2="chB" operator="arithmetic" k1="0" k2="1" k3="1" k4="0" />
          </filter>
        </defs>
      </svg>
    </>
  );
}

export default PillGlass;
